import { useCallback, useEffect, useState } from 'react';
import { RequestedBillList } from '.';
import { InputButton, BaseInput } from '../common';
import { FeedbackMessageType } from '../../models/types';
import { useTypedSelector, useActions } from '../../hooks';
import '../styles/RequestBillFormStyle.css';
import { BatuWS } from '../../batu/impl/BatuWebsockets';
import { v4 as uuidv4 } from 'uuid';
import { MassUpload } from '.';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth';

import {
  withAuthenticator,
  WithAuthenticatorProps,
} from '@aws-amplify/ui-react';

let baseURL = process.env.REACT_APP_API_WSS_BASE_URL ?? '';

const MASS_UPLOAD_DELAY_BETWEEN_REQS = 1000;
interface ScraperFormProps extends WithAuthenticatorProps {
  setMessage(message: any, type?: FeedbackMessageType): void;
}
const RequestBillForm: React.FC<ScraperFormProps> = ({ setMessage }) => {
  const [variableErrors, setVariableErrors] = useState(['']);
  const rpu = useTypedSelector(({ bills }) => {
    return bills.rpu;
  });
  const requestedBills = useTypedSelector(({ bills }) => {
    return bills.requestedBills;
  });
  const massUploadRPUList = useTypedSelector(({ bills }) => {
    return bills.massUploadRPUList;
  });
  const massUploadFile = useTypedSelector(({ bills }) => {
    return bills.massUploadFile;
  });
  const { addRpuToRequestedBills, updateRPU } = useActions();
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const requestMassUploadRPUList = useCallback(async () => {
    const inc = async (i: number) => {
      await delay(MASS_UPLOAD_DELAY_BETWEEN_REQS);
      return ++i;
    };
    for (let i = 0; i < massUploadRPUList.length; i = await inc(i)) {
      let id = uuidv4();
      let _rpu = massUploadRPUList[i].rpu.toString().padStart(12, '0');
      let _serviceName = massUploadRPUList[i].serviceName?.toString() ?? null;
      let _latestTotal = massUploadRPUList[i].latestTotal;
      const [sessionToken, email]: [string, string] =
        await fetchAuthSession().then((session: AuthSession) => {
          const email = JSON.stringify(
            session.tokens?.idToken?.payload?.email
          ).replace(/['"]+/g, '');
          return [session.tokens?.idToken?.toString() ?? '', email];
        });
      const batu = new BatuWS(baseURL, _rpu, id, email);
      batu.requestRpu(sessionToken, _serviceName, _latestTotal);
      addRpuToRequestedBills(batu);
    }
  }, [addRpuToRequestedBills, massUploadRPUList]);
  const validateForm = useCallback(async () => {
    setMessage('', FeedbackMessageType.SUCCESS);
    setVariableErrors(['']);
    if (massUploadRPUList && massUploadRPUList.length > 0) {
      requestMassUploadRPUList();
      return;
    }
    if (rpu === '') {
      setMessage('Ingresa un número de servicio', FeedbackMessageType.ERROR);
      setVariableErrors(['rpu']);
      return;
    }
    if (rpu.length !== 12) {
      setMessage(
        'El número de servicio debe contener 12 números',
        FeedbackMessageType.ERROR
      );
      setVariableErrors(['rpu']);
      return;
    }
    let numRegex: RegExp = /^-?\d+$/;
    if (!numRegex.test(rpu)) {
      setMessage('Ingresa un número válido', FeedbackMessageType.ERROR);
      setVariableErrors(['rpu']);
      return;
    }
    let id = uuidv4();
    const [sessionToken, email]: [string, string] =
      await fetchAuthSession().then((session: AuthSession) => {
        const email = JSON.stringify(
          session.tokens?.idToken?.payload?.email
        ).replace(/['"]+/g, '');
        return [session.tokens?.idToken?.toString() ?? '', email];
      });
    const batu = new BatuWS(baseURL, rpu, id, email);
    batu.requestRpu(sessionToken);
    addRpuToRequestedBills(batu);
  }, [
    rpu,
    massUploadRPUList,
    addRpuToRequestedBills,
    setMessage,
    requestMassUploadRPUList,
  ]);
  //Catching submit events
  useEffect(() => {
    const listener = (event: any) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault();
        validateForm();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [validateForm]);
  const resetFeedback = () => {
    setVariableErrors(['']);
    setMessage('', FeedbackMessageType.SUCCESS);
  };
  const getRpuDisabledStatus = () => {
    if (massUploadRPUList.length > 0) {
      return true;
    }
  };
  const getRPuValue = () => {
    if (massUploadFile && massUploadRPUList.length > 0) {
      return massUploadFile?.name;
    }
    return rpu;
  };
  const renderContent = () => {
    return (
      <div className="request-bill-form">
        <p className="request-bill-form-title">Recibos</p>
        <div className="request-bill-form-input-container">
          <BaseInput
            inputLabel="Número de Servicio*"
            val={getRPuValue()}
            maxLength={12}
            disabled={getRpuDisabledStatus()}
            inputPlaceholder="0132456789"
            inError={variableErrors?.includes('rpu')}
            onTextChange={(val) => {
              updateRPU(val);
              resetFeedback();
            }}
            inputButton={
              <InputButton
                content="Enviar"
                disabled={false}
                onSubmit={() => validateForm()}
              />
            }
          />
          <MassUpload />
        </div>
      </div>
    );
  };
  const renderList = () => {
    if (requestedBills && requestedBills.length > 0) {
      return (
        <div className="request-bill-form-list-container">
          <RequestedBillList />
        </div>
      );
    }
    return null;
  };
  return (
    <div className="request-bill-form-container">
      {renderContent()}
      {renderList()}
    </div>
  );
};
export default withAuthenticator(RequestBillForm);
